import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/contexts/AuthContext'
import { LoadingSpinner } from '@/components/ui/loading-spinner'

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordPage() {
  const { resetPassword } = useAuth()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    if (isSubmitting) return
    setIsSubmitting(true)

    try {
      const { error } = await resetPassword(data.email)

      if (error) {
        // Log for diagnosis but do not reveal details to the user (security -
        // don't leak whether the email exists, rate-limit state, etc.)
        console.error('Password reset request failed:', error)
        toast({
          title: 'Unable to send reset link',
          description:
            'We could not send a reset email right now. Please try again in a few minutes or contact support if the problem persists.',
          variant: 'destructive',
        })
        return
      }

      // Show success message (security - don't reveal if email exists)
      setIsSuccess(true)
      toast({ title: 'If an account exists, a reset link has been sent' })
    } catch (err) {
      console.error('Password reset request threw:', err)
      toast({ title: 'Error', description: 'An unexpected error occurred', variant: 'destructive' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="text-center">
        <h2 className="mb-4 text-2xl font-semibold">Check Your Email</h2>
        <p className="mb-6 text-muted-foreground">
          If an account exists with that email address, we've sent a password
          reset link.
        </p>
        <Link
          to="/login"
          className="text-sm font-medium text-primary hover:underline"
        >
          Back to Sign In
        </Link>
      </div>
    )
  }

  return (
    <div>
      <h2 className="mb-2 text-center text-2xl font-semibold">
        Forgot Password
      </h2>
      <p className="mb-6 text-center text-sm text-muted-foreground">
        Enter your email address and we'll send you a link to reset your
        password.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            {...register('email')}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="you@example.com"
            disabled={isSubmitting}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-destructive">
              {errors.email.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Sending...
            </>
          ) : (
            'Send Reset Link'
          )}
        </button>
      </form>

      <div className="mt-4 text-center">
        <Link
          to="/login"
          className="text-sm text-muted-foreground hover:text-foreground hover:underline"
        >
          Back to Sign In
        </Link>
      </div>
    </div>
  )
}
